import { KycSessionStorage } from '@/common/KycSessionStorage';
import {
  wellKnownKycSessionRoutes,
  type WellKnownKycSessionRoute,
} from '@/routes/wellKnownKycSessionRoutes';
import type { KycSessionData } from '@/stores/auth.store';
import type { KycSession } from './KycSession';
export enum StepSet {
  FULL = 'FULL',
  CONTACTDETAILS = 'CONTACTS',
  CONTACTS_WITH_PENDING_KYC = 'CONTACTS_WITH_PENDING_KYC',
}

export type StepName =
  | 'Region'
  | 'Consent'
  | 'Address'
  | 'ProofOfAddress'
  | 'ID Verification'
  | 'Contact'
  | 'Spending'
  | 'Confirmation';

export type Screen = WellKnownKycSessionRoute;

export type Step = {
  name: StepName;
  order: number;
  screens: Screen[];
};

export const KYC_STEPS: Step[] = [
  {
    name: 'Region',
    order: 1000,
    screens: [wellKnownKycSessionRoutes.Region],
  },
  {
    name: 'Consent',
    order: 2000,
    screens: [wellKnownKycSessionRoutes.Consent],
  },
  {
    name: 'Address',
    order: 4000,
    screens: [wellKnownKycSessionRoutes.Address],
  },
  {
    name: 'ID Verification',
    order: 5000,
    screens: [wellKnownKycSessionRoutes.Scans],
  },
  {
    name: 'Confirmation',
    order: 10000,
    screens: [
      wellKnownKycSessionRoutes.PendingReview,
      wellKnownKycSessionRoutes.Success,
      wellKnownKycSessionRoutes.Failed,
    ],
  },
];

export const KYC_STEPS_FOR_PENDING_KYC: Step[] = [
  {
    name: 'Confirmation',
    order: 10000,
    screens: [
      wellKnownKycSessionRoutes.PendingReview,
      wellKnownKycSessionRoutes.Success,
      wellKnownKycSessionRoutes.Failed,
    ],
  },
];

export const CONTACTS_AND_SPENDING_STEPS: Step[] = [
  {
    name: 'Contact',
    order: 3000,
    screens: [wellKnownKycSessionRoutes.Contact],
  },
  {
    name: 'Spending',
    order: 3500,
    screens: [wellKnownKycSessionRoutes.Spending],
  },
  {
    name: 'Confirmation',
    order: 9000,
    screens: [wellKnownKycSessionRoutes.Success],
  },
];

export const PROOF_OF_ADDRESS_STEPS: Step[] = [
  {
    name: 'ProofOfAddress',
    order: 4100,
    screens: [wellKnownKycSessionRoutes.ProofOfAddress],
  },
];

export class UserFlow {
  steps: Step[];
  screens: Screen[];
  initialStepSet: StepSet;
  constructor(steps: Step[], initialStepSet: StepSet) {
    this.steps = steps;
    this.screens = this.steps.flatMap((step) => step.screens);
    this.initialStepSet = initialStepSet;
  }

  getNextScreen(screen: Screen): Screen {
    const currentScreenIndex = this.screens.indexOf(screen);

    if (currentScreenIndex === -1 || currentScreenIndex === this.screens.length - 1) {
      return screen;
    }

    return this.screens[currentScreenIndex + 1] ?? screen;
  }

  getPreviousScreen(screen: Screen): Screen {
    const currentScreenIndex = this.screens.indexOf(screen);

    if (currentScreenIndex === -1 || currentScreenIndex === 0) {
      return screen;
    }

    return this.screens[currentScreenIndex - 1] ?? screen;
  }

  addScreenAfter(indexScreen: Screen, screenToAdd: Screen) {
    const index = this.screens.indexOf(indexScreen);
    if (index !== -1) {
      this.screens.splice(index + 1, 0, screenToAdd);
    } else {
      const idvIndex = this.screens.indexOf(wellKnownKycSessionRoutes.Scans);
      this.screens.splice(idvIndex, 0, screenToAdd);
    }
  }

  removeScreen(screenToRemove: Screen) {
    const index = this.screens.indexOf(screenToRemove);
    if (index !== -1) {
      this.screens.splice(index, 1);
    }
  }

  hasScreen(screen: Screen) {
    return this.screens.includes(screen);
  }

  monitorRegionChange(kycSession: KycSession) {
    const region = kycSession.region;
    if (!region) {
      return;
    }

    const hasProofOfAddress = this.hasScreen('ProofOfAddress');
    const shouldHaveProofOfAddress = kycSession.isPoaRequired();

    if (hasProofOfAddress === shouldHaveProofOfAddress) {
      return;
    }

    if (shouldHaveProofOfAddress) {
      this.addScreenAfter('Address', 'ProofOfAddress');
    } else {
      this.removeScreen('ProofOfAddress');
    }
  }

  getCurrentStepForScreen(screen: Screen): Step | undefined {
    return this.steps.find((step) => step.screens.includes(screen));
  }

  hasKycSteps() {
    return [StepSet.CONTACTS_WITH_PENDING_KYC, StepSet.FULL].includes(this.initialStepSet);
  }

  hasRegionStep() {
    return this.steps.some((step) => step.screens.includes(wellKnownKycSessionRoutes.Region));
  }

  static forKycSession(kycSession: KycSession): UserFlow {
    const sessionData = KycSessionStorage.getDataForSession<KycSessionData>(kycSession.id);
    let stepSet = sessionData?.initialStepSet;

    //TECH DEBT-322: https://www.notion.so/immersve/UserFlow-for-verify-immersve-com-is-calculated-on-the-frontend-2721d446ed8a80858c87e034b902c6d8?source=copy_link
    if (!stepSet) {
      if (kycSession.isKycPending()) {
        stepSet = StepSet.CONTACTS_WITH_PENDING_KYC;
      } else if (kycSession.isKycRequired()) {
        stepSet = StepSet.FULL;
      } else {
        stepSet = StepSet.CONTACTDETAILS;
      }
    }

    let combinedSteps: Step[] = [];

    if (stepSet === StepSet.CONTACTS_WITH_PENDING_KYC) {
      combinedSteps = UserFlow.combineSteps(KYC_STEPS_FOR_PENDING_KYC, CONTACTS_AND_SPENDING_STEPS);
    } else if (stepSet === StepSet.CONTACTDETAILS) {
      combinedSteps = CONTACTS_AND_SPENDING_STEPS;
    } else {
      combinedSteps = UserFlow.combineSteps(KYC_STEPS, CONTACTS_AND_SPENDING_STEPS);
    }

    if (stepSet !== StepSet.CONTACTDETAILS) {
      combinedSteps = UserFlow.combineSteps(combinedSteps, PROOF_OF_ADDRESS_STEPS);
    }

    //exclude configured hidden steps
    combinedSteps = combinedSteps.filter(
      (step) => !kycSession?.hiddenSteps || !kycSession.hiddenSteps.includes(step.name)
    );

    if (combinedSteps.length === 0) {
      throw new Error('No steps found for the user flow');
    }

    return new UserFlow(combinedSteps, stepSet);
  }

  static combineSteps(...stepSets: Step[][]): Step[] {
    const concatenatedSteps = stepSets.flat();

    const unsortedSteps = concatenatedSteps.filter((step) => {
      const duplicateSteps = concatenatedSteps.filter((s) => s.name === step.name);
      if (duplicateSteps.length > 1) {
        const highestOrder = Math.max(...duplicateSteps.map((s) => s.order));
        return step.order === highestOrder;
      }
      return step;
    });

    return unsortedSteps.sort((a, b) => a.order - b.order);
  }
}
